import { z } from 'zod'
import { toast } from 'sonner'
import axios from '@/lib/axios'
import { useForm } from 'react-hook-form'
import { Card } from '@/components/ui/card'
import { uploadFile } from '@/lib/firebase'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { MEDIA_LIMIT } from '@/constant/media-limit'
import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from '@/routes/hooks/use-router'
import { useParams } from '@/routes/hooks/use-params'
import React, { useRef, useState, useEffect } from 'react'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
// import { useAuthContext } from '@/contexts/auth-context/auth-provider'
import {
  Image as LucideImage,
  Video as LucideVideo,
  LucideX,
} from 'lucide-react'
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form'
import {
  FeedType,
  FeedPrivacy,
  type FeedCreatePostPayload,
  type SharedContent,
  MediaItem,
  type Feed,
} from '@/types/feed.type'

const { MAX_IMAGE, MAX_VIDEO, MAX_IMAGE_SIZE, MAX_VIDEO_SIZE } = MEDIA_LIMIT

const feedSchema = z.object({
  type: z.nativeEnum(FeedType),
  content: z.string().optional(),
  mediaItems: z
    .array(
      z.object({
        type: z.enum(['image', 'video']),
        url: z.string().url(),
        file: z
          .instanceof(File)
          .optional()
          .refine(
            (file) =>
              !file ||
              (file.type.startsWith('image/') && file.size <= MAX_IMAGE_SIZE) ||
              (file.type.startsWith('video/') && file.size <= MAX_VIDEO_SIZE),
            {
              message: 'ไฟล์รูปภาพหรือวิดีโอมีขนาดใหญ่เกินกำหนด',
            },
          ),
        thumbnailUrl: z.string().optional(),
        caption: z.string().optional(),
      }),
    )
    .optional(),
  sharedContent: z
    .object({
      type: z.enum(['article', 'livestream', 'feed']),
      contentId: z.string(),
      title: z.string().optional(),
      description: z.string().optional(),
      previewImageUrl: z.string().optional(),
      originalUrl: z.string().optional(),
      originalAuthor: z
        .object({
          userId: z.string(),
          displayName: z.string(),
          imageUrl: z.string().optional(),
        })
        .optional(),
    })
    .optional(),
  privacy: z.nativeEnum(FeedPrivacy),
  taggedUsers: z
    .array(
      z.object({
        userId: z.string(),
        displayName: z.string(),
      }),
    )
    .optional(),
  hashtags: z.array(z.string()).optional(),
})

type FeedFormType = z.infer<typeof feedSchema>

const defaultValues: Partial<FeedFormType> = {
  type: FeedType.TEXT,
  content: '',
  privacy: FeedPrivacy.PUBLIC,
  hashtags: [],
}

export default function FeedFormView() {
  const router = useRouter()
  const params = useParams()
  // const { user } = useAuthContext()
  const [isEditMode, setIsEditMode] = useState(false)
  const [feedData, setFeedData] = useState<Feed | null>(null)
  const [isLoadingFeed, setIsLoadingFeed] = useState(false)

  const form = useForm<FeedFormType>({
    resolver: zodResolver(feedSchema),
    defaultValues,
  })
  const [loading, setLoading] = useState(false)
  const [mediaPreview, setMediaPreview] = useState<{
    image: string[]
    video: string[]
  }>({ image: [], video: [] })
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [mediaType, setMediaType] = useState<'image' | 'video'>('image')

  // Check if we're in edit mode and fetch feed data
  useEffect(() => {
    const checkEditMode = async () => {
      const feedId = params?.id
      if (feedId && window.location.pathname.includes('/edit')) {
        setIsEditMode(true)
        setIsLoadingFeed(true)
        try {
          const response = await axios.get(`/feeds/${feedId}`)
          const feed = response.data
          setFeedData(feed)

          // Populate form with existing data
          form.reset({
            type: feed.type,
            content: feed.content || '',
            privacy: feed.privacy,
            hashtags: feed.hashtags || [],
            mediaItems: feed.mediaItems || [],
            sharedContent: feed.sharedContent,
          })

          // Set media previews
          if (feed.mediaItems && feed.mediaItems.length > 0) {
            const images = feed.mediaItems
              .filter((item: MediaItem) => item.type === 'image')
              .map((item: MediaItem) => item.url)
            const videos = feed.mediaItems
              .filter((item: MediaItem) => item.type === 'video')
              .map((item: MediaItem) => item.url)

            setMediaPreview({
              image: images,
              video: videos,
            })
          }
        } catch (error) {
          console.error('Error fetching feed:', error)
          toast.error('ไม่สามารถโหลดข้อมูลโพสต์ได้')
        } finally {
          setIsLoadingFeed(false)
        }
      }
    }

    checkEditMode()
  }, [params?.id, form])

  // handle file upload
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const type = mediaType
    const maxCount = type === 'image' ? MAX_IMAGE : MAX_VIDEO
    const maxSize = type === 'image' ? MAX_IMAGE_SIZE : MAX_VIDEO_SIZE
    const acceptType = type === 'image' ? /^image\// : /^video\//
    let error = ''
    const validFiles: File[] = []
    for (let i = 0; i < files.length; i++) {
      const file = files[i]
      if (!acceptType.test(file.type)) {
        error = `ไฟล์ที่เลือกไม่ใช่${type === 'image' ? 'รูปภาพ' : 'วิดีโอ'}`
        break
      }
      if (file.size > maxSize) {
        error = `ขนาดไฟล์ต้องไม่เกิน ${type === 'image' ? '5MB' : '50MB'}`
        break
      }
      validFiles.push(file)
      if (validFiles.length >= maxCount) break
    }
    if (error) {
      toast.error(error)
      return
    }
    // รวมกับไฟล์เดิม (ถ้ามี)
    const current = form.getValues('mediaItems') || []
    // filter เฉพาะ type เดียวกัน
    const currentType = current.filter((item) => item.type === type)
    // จำกัดจำนวนสูงสุด
    if (currentType.length + validFiles.length > maxCount) {
      toast.error(`อัปโหลดได้สูงสุด ${maxCount} ไฟล์`)
      return
    }
    let urls: string[]
    if (type === 'video') {
      const images = []
      // ดึง thumbnail ของวิดีโอแต่ละไฟล์
      for (const file of validFiles) {
        const url = URL.createObjectURL(file)
        // ดึง thumbnail (frame แรก) ของวิดีโอ
        const getThumbnail = (videoUrl: string): Promise<string> =>
          new Promise((resolve) => {
            const video = document.createElement('video')
            video.src = videoUrl
            video.crossOrigin = 'anonymous'
            video.currentTime = 0.1 // jump ไป frame แรก
            video.muted = true
            video.playsInline = true
            video.addEventListener('loadeddata', () => {
              const canvas = document.createElement('canvas')
              canvas.width = video.videoWidth
              canvas.height = video.videoHeight
              const ctx = canvas.getContext('2d')
              if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
                resolve(canvas.toDataURL('image/png'))
              } else {
                resolve('')
              }
            })
            video.addEventListener('error', () => resolve(''))
          })

        // ดึง thumbnail แบบ async แล้ว push เข้า array
        images.push(await getThumbnail(url))
      }
      urls = images
    } else {
      urls = validFiles.map((file) => URL.createObjectURL(file))
    }
    setMediaPreview((prev) => ({
      ...prev,
      [type]: [...prev[type], ...urls].slice(0, maxCount),
    }))
    // อัปเดต mediaItems ใน form (รวมทั้ง image และ video)
    const otherType = type === 'image' ? 'video' : 'image'
    const otherItems = current.filter((item) => item.type === otherType)
    form.setValue('mediaItems', [
      ...otherItems,
      ...[
        ...currentType,
        ...validFiles.map((file, idx) => ({ type, file, url: urls[idx] })),
      ].slice(0, maxCount),
    ])
  }

  // handle hashtag input
  const [hashtagInput, setHashtagInput] = useState('')
  const handleAddHashtag = () => {
    const tag = hashtagInput.trim()
    // ถ้า tag ขึ้นต้นด้วย # ให้ลบ # ตัวแรกออก
    let cleanTag = tag
    if (tag.startsWith('#')) {
      cleanTag = tag.slice(1)
    }

    if (cleanTag && !form.getValues('hashtags')?.includes(cleanTag)) {
      form.setValue('hashtags', [
        ...(form.getValues('hashtags') || []),
        cleanTag,
      ])
      setHashtagInput('')
    }
  }

  const handleRemoveHashtag = (tag: string) => {
    form.setValue(
      'hashtags',
      (form.getValues('hashtags') || []).filter((t) => t !== tag),
    )
  }

  // handle submit
  const onSubmit = async (values: FeedFormType) => {
    setLoading(true)
    try {
      // Clean up payload ตาม type
      let payload: Partial<FeedCreatePostPayload>
      const hashtags = values.hashtags || []
      const taggedUsers = values.taggedUsers || []
      if (values.type === FeedType.TEXT) {
        payload = {
          ...values,
          mediaItems: [],
          taggedUsers,
          hashtags,
        }
        delete payload.sharedContent
      } else if (
        values.type === FeedType.IMAGE ||
        values.type === FeedType.VIDEO
      ) {
        const items: MediaItem[] = values.mediaItems
          ? await Promise.all(
              values.mediaItems.map(async (item) => {
                let imageUrl = ''
                if (item?.file) {
                  imageUrl = await uploadFile(
                    item?.file,
                    'feed-medias',
                    `${item.file?.name || Math.floor(Math.random() * 1000000)}-${Date.now()}`,
                  )
                }

                return {
                  type: item.type,
                  url: imageUrl,
                }
              }),
            )
          : []

        payload = {
          ...values,
          mediaItems: items,
          taggedUsers,
          hashtags,
        }
        delete payload.sharedContent
      } else {
        payload = {
          ...values,
          mediaItems: [],
          sharedContent: values.sharedContent as SharedContent,
          taggedUsers,
          hashtags,
        }
      }
      // Remove undefined fields (type-safe)
      Object.keys(payload).forEach((k) => {
        const key = k as keyof FeedCreatePostPayload
        if (payload[key] === undefined) {
          delete payload[key]
        }
      })

      if (isEditMode && feedData) {
        // Update existing feed - preserve existing media items
        const updatePayload = {
          ...payload,
          mediaItems: feedData.mediaItems || [], // Keep existing media items
        }
        await axios.patch(
          `/feeds/${feedData.id}`,
          updatePayload as FeedCreatePostPayload,
        )
        toast.success('แก้ไขโพสต์สำเร็จ')
      } else {
        // Create new feed
        await axios.post('/feeds', payload as FeedCreatePostPayload)
        toast.success('โพสต์สำเร็จ')
      }

      form.reset(defaultValues)
      setMediaPreview({ image: [], video: [] })
      router.back()
    } catch (e) {
      toast.error((e as Error)?.message || 'เกิดข้อผิดพลาด')
    } finally {
      setLoading(false)
    }
  }

  // const name = user
  //   ? user.firstName
  //     ? `${user.firstName} ${user.lastName}`
  //     : user.displayName
  //   : ''

  // Show loading state while fetching feed data
  if (isLoadingFeed) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <Card className="p-6 bg-white border border-gray-200 shadow-sm">
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-gray-600">กำลังโหลดข้อมูลโพสต์...</p>
            </div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto p-6">
      <Card className="p-6 bg-white border border-gray-200 shadow-sm">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            {isEditMode ? 'แก้ไขโพสต์' : 'สร้างโพสต์ใหม่'}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? 'แก้ไขโพสต์ของคุณ' : 'แชร์ความคิดของคุณกับชุมชน'}
          </p>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6"
            autoComplete="off"
          >
            {/* Post Type Selection with Icons - Only show in create mode */}
            {!isEditMode && (
              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                      ประเภทโพสต์
                    </FormLabel>
                    <FormControl>
                      <div className="grid grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => field.onChange(FeedType.TEXT)}
                          className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                            field.value === FeedType.TEXT
                              ? 'border-blue-500 bg-blue-500/10 text-blue-600'
                              : 'border-gray-300 bg-white hover:border-gray-400 text-gray-600'
                          }`}
                        >
                          <svg
                            className="w-6 h-6 mb-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M2 4a2 2 0 012-2h12a2 2 0 012 2v12a2 2 0 01-2 2H4a2 2 0 01-2-2V4zm3 2h10v2H5V6zm0 4h10v2H5v-2zm0 4h6v2H5v-2z" />
                          </svg>
                          <span className="text-sm font-medium">ข้อความ</span>
                        </button>

                        <button
                          type="button"
                          onClick={() => field.onChange(FeedType.IMAGE)}
                          className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${
                            field.value === FeedType.IMAGE
                              ? 'border-blue-500 bg-blue-500/10 text-blue-600'
                              : 'border-gray-300 bg-white hover:border-gray-400 text-gray-600'
                          }`}
                        >
                          <svg
                            className="w-6 h-6 mb-2"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z"
                              clipRule="evenodd"
                            />
                          </svg>
                          <span className="text-sm font-medium">
                            รูปภาพ/วิดีโอ
                          </span>
                        </button>
                      </div>
                    </FormControl>
                  </FormItem>
                )}
              />
            )}

            {/* Content Textarea */}
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-gray-700 font-medium">
                    เขียนอะไรบางอย่าง...
                  </FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Textarea
                        maxLength={2000}
                        placeholder="คุณกำลังคิดอะไรอยู่..."
                        className="bg-white border border-gray-200 rounded-lg px-4 py-3 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                        {...field}
                      />
                      <div className="absolute -bottom-[18px] right-3 text-xs text-gray-500">
                        {field.value?.length || 0}/500
                      </div>
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* IMAGE/VIDEO Upload - Only show in create mode */}
            {!isEditMode &&
              (form.watch('type') === FeedType.IMAGE ||
                form.watch('type') === FeedType.VIDEO) && (
                <FormItem>
                  <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                    <LucideImage className="w-5 h-5" />
                    อัปโหลดรูปภาพ/วิดีโอ
                  </FormLabel>
                  <FormControl>
                    <Tabs
                      defaultValue={mediaType}
                      onValueChange={(val) =>
                        setMediaType(val as 'image' | 'video')
                      }
                      className="w-full"
                    >
                      <TabsList className="mb-2">
                        <TabsTrigger
                          value="image"
                          className="flex items-center gap-2"
                        >
                          <LucideImage className="w-4 h-4" /> รูปภาพ{' '}
                          {mediaPreview.image.length
                            ? `(${mediaPreview.image.length})`
                            : ''}
                        </TabsTrigger>
                        <TabsTrigger
                          value="video"
                          className="flex items-center gap-2"
                        >
                          <LucideVideo className="w-4 h-4" /> วิดีโอ{' '}
                          {mediaPreview.video.length
                            ? `(${mediaPreview.video.length})`
                            : ''}
                        </TabsTrigger>
                      </TabsList>
                      <TabsContent value="image">
                        <div className="space-y-3">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors bg-gray-50">
                            <Input
                              type="file"
                              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/heic,image/heif"
                              multiple
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setMediaType('image')
                                fileInputRef.current?.click()
                              }}
                              className="flex flex-col items-center gap-2 w-full"
                            >
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <LucideImage className="w-4 h-4 text-blue-500" />
                              </div>
                              <div className="text-gray-600">
                                <p className="text-sm font-medium">
                                  เพิ่มรูปภาพ (ไม่เกิน{' '}
                                  {MAX_IMAGE_SIZE / 1024 / 1024}MB)
                                </p>
                              </div>
                            </button>
                          </div>
                          {mediaPreview.image.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {mediaPreview.image.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <img
                                    src={url}
                                    alt="preview"
                                    className="w-full h-32 object-cover rounded-lg"
                                  />
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMediaPreview((prev) => ({
                                        ...prev,
                                        image: prev.image.filter(
                                          (_, i) => i !== idx,
                                        ),
                                      }))
                                      // remove only image type at idx
                                      const items = (
                                        form.getValues('mediaItems') || []
                                      ).filter(
                                        (item, i) =>
                                          !(item.type === 'image' && i === idx),
                                      )
                                      form.setValue('mediaItems', items)
                                    }}
                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center"
                                  >
                                    <LucideX className="w-4 h-4 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                      <TabsContent value="video">
                        <div className="space-y-3">
                          <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-500 transition-colors bg-gray-50">
                            <Input
                              type="file"
                              accept="video/*"
                              ref={fileInputRef}
                              onChange={handleFileChange}
                              className="hidden"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                setMediaType('video')
                                fileInputRef.current?.click()
                              }}
                              className="flex flex-col items-center gap-2 w-full"
                            >
                              <div className="w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                <LucideVideo className="w-4 h-4 text-blue-500" />
                              </div>
                              <div className="text-gray-600">
                                <p className="text-sm font-medium">
                                  เพิ่มวิดีโอ (ไม่เกิน{' '}
                                  {MAX_VIDEO_SIZE / 1024 / 1024}MB)
                                </p>
                              </div>
                            </button>
                          </div>
                          {mediaPreview.video.length > 0 && (
                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                              {mediaPreview.video.map((url, idx) => (
                                <div key={idx} className="relative group">
                                  <div className="w-full h-32 rounded-lg bg-black flex items-center justify-center overflow-hidden">
                                    <img
                                      src={url}
                                      alt="video thumbnail"
                                      className="object-cover w-full h-full"
                                    />
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                      <div className="bg-black/60 rounded-full p-2">
                                        <LucideVideo className="w-8 h-8 text-white opacity-80" />
                                      </div>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setMediaPreview((prev) => ({
                                        ...prev,
                                        video: prev.video.filter(
                                          (_, i) => i !== idx,
                                        ),
                                      }))
                                      // remove only video type at idx
                                      const items = (
                                        form.getValues('mediaItems') || []
                                      ).filter(
                                        (item, i) =>
                                          !(item.type === 'video' && i === idx),
                                      )
                                      form.setValue('mediaItems', items)
                                    }}
                                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 hover:bg-red-600 rounded-full flex items-center justify-center"
                                  >
                                    <LucideX className="w-4 h-4 text-white" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>
                    </Tabs>
                  </FormControl>
                </FormItem>
              )}

            {/* Show existing media in edit mode */}
            {isEditMode &&
              feedData?.mediaItems &&
              feedData.mediaItems.length > 0 && (
                <FormItem>
                  <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                    <LucideImage className="w-5 h-5" />
                    ไฟล์แนบ (ไม่สามารถแก้ไขได้)
                  </FormLabel>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {feedData.mediaItems.map((item, idx) => (
                      <div key={idx} className="relative">
                        {item.type === 'image' ? (
                          <img
                            src={item.url}
                            alt={`media-${idx}`}
                            className="w-full h-32 object-cover rounded-lg border border-gray-200"
                          />
                        ) : (
                          <div className="w-full h-32 rounded-lg bg-black flex items-center justify-center overflow-hidden border border-gray-200">
                            <video
                              src={item.url}
                              className="object-cover w-full h-full"
                              controls={false}
                              preload="metadata"
                            />
                            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                              <div className="bg-black/60 rounded-full p-2">
                                <LucideVideo className="w-8 h-8 text-white opacity-80" />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-2">
                    ไฟล์แนบจะยังคงอยู่ในโพสต์หลังจากแก้ไข
                  </p>
                </FormItem>
              )}

            {/* Hashtags */}
            <FormItem>
              <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M7 20l4-16m2 16l4-16M6 9h14M4 15h14"
                  />
                </svg>
                แฮชแท็ก
              </FormLabel>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <Input
                    disabled={(form.watch('hashtags') || []).length >= 10}
                    value={hashtagInput}
                    onChange={(e) => setHashtagInput(e.target.value)}
                    placeholder="เพิ่ม แฮชแท็ก..."
                    className="flex-1 h-12 bg-white border border-gray-200 rounded-lg px-3 py-2 text-gray-900 placeholder-gray-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        handleAddHashtag()
                      }
                    }}
                  />
                  <Button
                    disabled={(form.watch('hashtags') || []).length >= 10}
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleAddHashtag}
                    className="bg-blue-500 hover:bg-blue-600 border-blue-400 !text-white px-4 min-h-12"
                  >
                    เพิ่ม
                  </Button>
                </div>

                {(form.watch('hashtags') || []).length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {(form.watch('hashtags') || []).map((tag) => (
                      <span
                        key={tag}
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-400 bg-blue-500/20 px-3 py-1 rounded-full hover:bg-blue-500/30 cursor-pointer transition-colors"
                        onClick={() => handleRemoveHashtag(tag)}
                      >
                        #{tag}
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M6 18L18 6M6 6l12 12"
                          />
                        </svg>
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </FormItem>

            {/* Privacy */}
            <FormField
              control={form.control}
              name="privacy"
              render={() => (
                <FormItem>
                  <FormLabel className="text-gray-700 font-medium flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    ความเป็นส่วนตัว
                  </FormLabel>
                  <FormControl>
                    <div
                      className="grid gap-3"
                      style={{
                        gridTemplateColumns:
                          'repeat(auto-fit, minmax(120px, 1fr))',
                      }}
                    >
                      {Object.values(FeedPrivacy).map((privacy) => (
                        <Button
                          key={privacy}
                          type="button"
                          variant={
                            form.watch('privacy') === privacy
                              ? 'default'
                              : 'outline'
                          }
                          size="sm"
                          className={`${
                            form.watch('privacy') === privacy
                              ? 'bg-blue-500 hover:bg-blue-600 text-white border-blue-500'
                              : 'border-gray-300 bg-white hover:bg-gray-50 text-gray-700'
                          }`}
                          onClick={() => form.setValue('privacy', privacy)}
                        >
                          {privacy === FeedPrivacy.PUBLIC && 'สาธารณะ'}
                          {privacy === FeedPrivacy.FRIENDS && 'เพื่อนเท่านั้น'}
                          {privacy === FeedPrivacy.PRIVATE && 'ส่วนตัว'}
                        </Button>
                      ))}
                    </div>
                  </FormControl>
                </FormItem>
              )}
            />

            {/* Action Buttons */}
            <div className="flex gap-3 pt-6 border-t border-gray-200">
              <Button
                type="submit"
                disabled={loading}
                className="bg-blue-500 hover:bg-blue-600 border-blue-400 !text-white px-6 py-3 min-h-12 flex-1"
              >
                {loading
                  ? isEditMode
                    ? 'กำลังแก้ไข...'
                    : 'กำลังโพสต์...'
                  : isEditMode
                    ? 'แก้ไขโพสต์'
                    : 'โพสต์'}
              </Button>
            </div>
          </form>
        </Form>
      </Card>
    </div>
  )
}
